/**
 * La lingua della console.
 *
 * # La scelta che decide tutto: la chiave è l'italiano
 *
 * Non ci sono identificatori (`pagina.utenti.titolo`): la chiave di ogni testo
 * **è il testo italiano**, e le altre lingue sono un dizionario da quello.
 *
 * Un catalogo di identificatori sarebbe un secondo vocabolario da tenere
 * allineato al primo — millecinquecento nomi inventati che vivono lontano dal
 * punto in cui si usano, e che divergono al primo rinominamento. È esattamente
 * la famiglia di difetti che questo progetto passa il tempo a chiudere: due
 * metà che devono incontrarsi e nessuno che verifichi che si incontrino.
 *
 * Con l'italiano come chiave:
 *
 * - **il ripiego è leggibile**. Una traduzione che manca mostra la frase
 *   italiana, non `pagina.utenti.titolo`. Su una console mezza tradotta è la
 *   differenza fra «capisco lo stesso» e «è rotta»;
 * - **il testo sta dove si usa**, quindi si legge insieme al codice che lo
 *   mostra, come tutti i commenti di questo progetto;
 * - **niente da inventare**: non si sceglie un nome, si scrive la frase.
 *
 * Il prezzo è che cambiare la frase italiana scollega la traduzione senza
 * rumore. Non si può evitare — si può però **vedere**: la prova
 * `test_traduzioni.py` elenca le voci inglesi che non corrispondono più a
 * nessuna frase del sorgente, e le frasi che l'inglese non ha ancora.
 *
 * # Come si aggiunge una lingua
 *
 * Un file in `dizionari/`, una voce in `LINGUE`. Nient'altro: nessun
 * componente cambia, perché nessun componente conosce le lingue.
 */

import { en } from './dizionari/en'
import { fr } from './dizionari/fr'

export type Lingua = 'it' | 'en' | 'fr'

/** Le lingue disponibili, con il nome **nella lingua stessa**.
 *
 * Nella lingua stessa e non tradotto: chi cerca la propria lingua in un elenco
 * la cerca come la chiama lui. «Inglese» non aiuta chi non sa l'italiano, ed è
 * precisamente la persona che sta usando quel menu.
 */
export const LINGUE: { value: Lingua; label: string }[] = [
  { value: 'it', label: 'Italiano' },
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'Français' },
]

const CHIAVE = 'censo.lingua'

/** I dizionari. L'italiano non ne ha uno: è la lingua sorgente. */
const DIZIONARI: Record<Lingua, Record<string, string>> = { it: {}, en, fr }

/**
 * La lingua scelta, o quella del browser, o l'italiano.
 *
 * L'ordine conta: una scelta esplicita non si scavalca mai, e chi non ha
 * scelto niente merita di partire dalla propria lingua invece che dalla nostra.
 */
export function leggiLingua(): Lingua {
  try {
    const salvata = localStorage.getItem(CHIAVE)
    if (salvata === 'it' || salvata === 'en' || salvata === 'fr') return salvata
  } catch {
    // Modalità privata o storage negato: si prosegue con il browser.
  }
  const preferite = typeof navigator !== 'undefined' ? navigator.languages ?? [] : []
  for (const codice of preferite) {
    const breve = codice.toLowerCase().slice(0, 2)
    if (breve === 'it' || breve === 'en' || breve === 'fr') return breve
  }
  return 'it'
}

export function scriviLingua(lingua: Lingua): void {
  try {
    localStorage.setItem(CHIAVE, lingua)
  } catch {
    // Vedi sopra: la scelta vale per questa sessione e basta.
  }
}

let corrente: Lingua = leggiLingua()

/** Cambia la lingua e ricarica: nessun componente deve ricordarsi di reagire.
 *
 * Ricaricare è grossolano e corretto: la lingua cambia due volte nella vita di
 * un utente, e un contesto reattivo attraverso cinquantotto componenti sarebbe
 * un meccanismo che si può sbagliare in cinquantotto punti per un evento che
 * non capita.
 */
export function cambiaLingua(lingua: Lingua): void {
  scriviLingua(lingua)
  corrente = lingua
  window.location.reload()
}

export function linguaCorrente(): Lingua {
  return corrente
}

/** Il locale per `toLocaleString` e `Intl`: numeri e date nella lingua scelta,
 *  non sempre all'italiana. */
export function localeCorrente(): string {
  return corrente === 'en' ? 'en-GB' : corrente === 'fr' ? 'fr-FR' : 'it-IT'
}

/**
 * Traduce una frase, sostituendo i segnaposto `{nome}`.
 *
 * I segnaposto e non la concatenazione: «trovati {quanti} apparati» si traduce,
 * `'trovati ' + n + ' apparati'` no — l'ordine delle parole cambia con la
 * lingua, e in tedesco il numero finisce da un'altra parte.
 */
export function t(frase: string, valori?: Record<string, unknown>): string {
  const voce = DIZIONARI[corrente][frase]
  if (voce === undefined) segnaMancante(frase)
  const tradotta = voce ?? frase
  if (!valori) return tradotta
  return tradotta.replace(/\{(\w+)\}/g, (intero, nome: string) => {
    // Un segnaposto senza valore resta com'è. Sostituirlo con «undefined»
    // scriverebbe quella parola nel messaggio, e chi la legge non ha modo di
    // sapere che era un valore mancante: sembrerebbe testo.
    if (!(nome in valori)) return intero
    const valore = valori[nome]
    return valore === null || valore === undefined ? intero : String(valore)
  })
}

/**
 * Le frasi che il dizionario non ha, raccolte mentre la console gira.
 *
 * Serve in sviluppo: un testo che nessuno ha tradotto non dà errore — mostra
 * l'italiano, ed è giusto — quindi senza questo elenco l'unico modo di trovarlo
 * sarebbe leggere ogni schermata in inglese. La prova che confronta sorgente e
 * dizionario fa il resto, ma questa raccoglie anche le frasi composte a tempo
 * di esecuzione, che il sorgente non mostra.
 */
export const mancanti = new Set<string>()

/** Registra una frase non tradotta, senza cambiare cosa si vede.
 *
 * Chiamata da `t` invece di avvolgere il dizionario in un proxy: il proxy
 * costava un controllo su **ogni** lettura, in produzione come in sviluppo, per
 * un elenco che serve solo a chi traduce. Un costo pagato da tutti per un
 * beneficio di pochi è il modo in cui una console diventa lenta senza che
 * nessuno sappia dire dove.
 */
function segnaMancante(frase: string): void {
  if (corrente !== 'it') mancanti.add(frase)
}

/**
 * La prima lettera maiuscola, per le etichette costruite da valori.
 *
 * Le fasce di rischio e le gravità arrivano dall'API come parole minuscole —
 * «critico», «alta» — e sono anche chiavi di traduzione, quindi non si possono
 * capitalizzare alla fonte senza rompere il dizionario. Si capitalizza al
 * momento di mostrarle: un'etichetta inizia con la maiuscola, un valore no.
 */
export function maiuscola(testo: string): string {
  return testo ? testo.charAt(0).toLocaleUpperCase() + testo.slice(1) : testo
}
